const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window is undefined'));
      return;
    }
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
};

export const loadTesseract = async () => {
  if (typeof window !== 'undefined' && window.Tesseract) {
    return window.Tesseract;
  }
  await loadScript('https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js');
  return window.Tesseract;
};

// Formats a YYYY-MM-DD or DD-MM-YYYY string into a human-readable English date with exact Day Month Year
export const formatReadableDate = (dateStr) => {
  if (!dateStr) return '';
  const trimmed = String(dateStr).trim();
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const parts = trimmed.split(/[-./\s]+/);
  if (parts.length === 3) {
    let day, monthIndex, year;
    if (parts[0].length === 4) {
      // YYYY-MM-DD format
      year = parts[0];
      monthIndex = parseInt(parts[1], 10) - 1;
      day = parseInt(parts[2], 10);
    } else if (parts[2].length === 4) {
      // DD-MM-YYYY format
      day = parseInt(parts[0], 10);
      monthIndex = parseInt(parts[1], 10) - 1;
      year = parts[2];
    } else {
      day = parseInt(parts[0], 10);
      monthIndex = parseInt(parts[1], 10) - 1;
      year = parts[2];
    }

    if (!isNaN(day) && !isNaN(monthIndex) && monthIndex >= 0 && monthIndex < 12 && year) {
      return `${day} ${months[monthIndex]} ${year}`;
    }
  }
  return trimmed;
};

const MONTHS_MAP = {
  // English
  january: 1, jan: 1,
  february: 2, feb: 2,
  march: 3, mar: 3,
  april: 4, apr: 4,
  may: 5,
  june: 6, jun: 6,
  july: 7, jul: 7,
  august: 8, aug: 8,
  september: 9, sep: 9, sept: 9,
  october: 10, oct: 10,
  november: 11, nov: 11,
  december: 12, dec: 12,

  // Telugu
  జనవరి: 1, ఫిబ్రవరి: 2, మార్చి: 3, ఏప్రిల్: 4, మే: 5, జూన్: 6, జూలై: 7,
  ఆగస్టు: 8, ఆగష్టు: 8, సెప్టెంబర్: 9, సెప్టెంబరు: 9, అక్టోబర్: 10,
  నవంబర్: 11, నవంబరు: 11, డిసెంబర్: 12, డిసెంబరు: 12,

  // Hindi
  जनवरी: 1, फ़रवरी: 2, फरवरी: 2, मार्च: 3, अप्रैल: 4, मई: 5, जून: 6,
  जुलाई: 7, अगस्त: 8, सितंबर: 9, सितम्बर: 9, अक्टूबर: 10, अक्तूबर: 10,
  नवंबर: 11, नवम्बर: 11, दिसंबर: 12, दिसम्बर: 12,

  // Kannada
  ಜನವರಿ: 1, ಫೆಬ್ರವರಿ: 2, ಮಾರ್ಚ್: 3, ಏಪ್ರಿಲ್: 4, ಮೇ: 5, ಜೂನ್: 6, ಜುಲೈ: 7,
  ಆಗಸ್ಟ್: 8, ಸೆಪ್ಟೆಂಬರ್: 9, ಅಕ್ಟೋಬರ್: 10, ನವೆಂಬರ್: 11, ಡಿಸೆಂಬರ್: 12,

  // Tamil
  ஜனவரி: 1, பிப்ரவரி: 2, மார்ச்: 3, ஏப்ரல்: 4, மே: 5, ஜூன்: 6, ஜூலை: 7,
  ஆகஸ்ட்: 8, செப்டம்பர்: 9, அக்டோபர்: 10, நவம்பர்: 11, டிசம்பர்: 12
};

// Check for negative context exclusions (DOB, Aadhaar, Phone, PIN, Survey, Doc No, Page No, etc.)
const isExcludedContext = (contextSnippet, fullTextLower) => {
  const snippet = contextSnippet.toLowerCase();

  // DOB Exclusion (unless document is birth certificate)
  const isBirthCert = fullTextLower.includes('birth certificate') || fullTextLower.includes('certificate of birth');
  if (!isBirthCert) {
    if (/\b(dob|d\.o\.b|date of birth|born|birth date)\b/i.test(snippet)) {
      return true;
    }
  }

  // Exclude numbers associated with IDs, Phones, PINs, Surveys, Doc Nos, Invoices, Page Nos
  if (/\b(aadhaar|uidai|aadhar|phone|mobile|tel|ph|pin|pincode|zip|survey|sy\.no|sy no|plot|door|flat|inv|invoice|page|pg|doc\.?\s*no|document\s*no|reg\.?\s*no|registration\s*no|ref\s*no|account\s*no)\b/i.test(snippet)) {
    return true;
  }

  return false;
};

// Calculate priority score for a verified date candidate
const calculateScore = (contextSnippet, pageNum, textLength, charIndex) => {
  let score = 50;
  const snippet = contextSnippet.toLowerCase();

  // 1. Direct "Dated <date>" or "Date: <date>" Header Prefix (Highest Priority Rule)
  const isDirectDatedHeader = /\b(dated|datad|date\s*:|dated\s*:|date\s*-|dated\s*-)\b/i.test(snippet);
  if (isDirectDatedHeader) {
    score += 150; // Massively boost direct Dated / Date headers
  } else if (/\b(date of document|document date|date issued|issued date|date of issue|report date|issue date|registration date|execution date|date of execution|made and executed|executed on|this deed|deed of sale|sale deed)/i.test(snippet)) {
    score += 60;
  } else if (/\b(date|dt:|dt\.|dt -)\b/i.test(snippet)) {
    score += 15;
  }

  // 2. Penalize cited reference dates (e.g., "Ref: Application ... on dt: 7-6-2010 and dt: 24 6-2010")
  if (/\b(ref:|reference|ref\.no|application\s+under|application\s+dated|letter\s+dated|memo\s+dated|proceedings\s+dated|order\s+dated|vide|cited|enclosures)\b/i.test(snippet)) {
    score -= 60;
  }

  // 3. Top Header Position Bonus (Top 35% of document is usually official letterhead date)
  if (textLength > 0) {
    const relPos = charIndex / textLength;
    if (relPos < 0.35) {
      score += 40; // Top header bonus
    } else if (relPos > 0.75) {
      score += 10;
    }
  }

  // 4. Page 1 priority
  if (pageNum === 1) {
    score += 50;
  } else if (pageNum === 2) {
    score += 10;
  }

  return score;
};

// Calendar day count helper
const isValidCalendarDate = (day, month, year) => {
  if (day < 1 || day > 31 || month < 1 || month > 12) return false;
  if (year < 1800 || year > 2099) return false;
  const daysInMonth = new Date(year, month, 0).getDate();
  return day <= daysInMonth;
};

// Strict date extraction & direct source verification from text
export const extractAllDatesFromText = (text, pageNum = 1, sourceName = 'PDF_TEXT') => {
  if (!text) return [];

  const candidates = [];
  // Normalize Unicode dashes (en-dash \u2013, em-dash \u2014, minus \u2212) to standard hyphen and normalize whitespace
  const normalized = text.replace(/[\u2010-\u2015\u2212]/g, '-').replace(/\s+/g, ' ');
  const textLower = normalized.toLowerCase();

  const addCandidate = (originalStr, day, month, year, matchIndex) => {
    // 1. Strict Calendar Verification
    if (!isValidCalendarDate(day, month, year)) {
      return;
    }

    // 1b. Check Context & Exclusions
    const start = Math.max(0, matchIndex - 30);
    const end = Math.min(normalized.length, matchIndex + originalStr.length + 30);
    const contextSnippet = normalized.substring(start, end).trim();

    const hasExplicitDateHeader = /\b(dated|datad|date\s*:|dated\s*:|date\s*-|dated\s*-|date of issue|issued date|date of document|execution date|registration date|date of execution)\b/i.test(contextSnippet);
    const currentYear = new Date().getFullYear();

    // Reject pre-1970 dates unless explicitly preceded by date headers
    if (year < 1970 && !hasExplicitDateHeader) {
      return;
    }
    // Reject future dates beyond currentYear + 1
    if (year > currentYear + 1) {
      return;
    }

    // Reject numbers separated purely by whitespace unless explicit date headers exist
    const isSpaceOnlySeparator = /^[0-9]{1,2}\s+[0-9]{1,2}\s+[0-9]{2,4}$/.test(originalStr.trim());
    if (isSpaceOnlySeparator && !hasExplicitDateHeader) {
      return;
    }

    // 2. Format Normalized Date
    const normYear = String(year);
    const normMonth = String(month).padStart(2, '0');
    const normDay = String(day).padStart(2, '0');
    const normalizedDate = `${normYear}-${normMonth}-${normDay}`;

    // 3. Check Exclusions
    if (isExcludedContext(contextSnippet, textLower)) {
      return;
    }

    // 4. Deduplicate against existing candidates
    if (candidates.some(c => c.normalized === normalizedDate && c.page === pageNum)) {
      return;
    }

    const score = calculateScore(contextSnippet, pageNum, normalized.length, matchIndex);
    const confidence = Math.min(0.99, Math.max(0.70, score / 100));

    candidates.push({
      original: originalStr.trim(),
      normalized: normalizedDate,
      type: 'document_date',
      page: pageNum,
      context: contextSnippet,
      confidence,
      score,
      source: sourceName,
      verified: true
    });
  };

  // Pattern A: Strict DMY Numeric (e.g. 15/08/2024, 22 -7-2010, Dated22-7-2010)
  const dmyRegex = /(?:^|[^0-9])([0-9]{1,2})[-./\s]+([0-9]{1,2})[-./\s]+([0-9]{2,4})\b/gi;
  let match;
  while ((match = dmyRegex.exec(normalized)) !== null) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    let year = parseInt(match[3], 10);
    if (year < 100) year += year < 50 ? 2000 : 1900;

    const matchedStr = match[0].replace(/^[^0-9]+/, ''); // Strip leading non-digit character if matched
    const matchIndex = match.index + (match[0].length - matchedStr.length);

    const cleanNum = matchedStr.replace(/[-./\s]/g, '');
    if (cleanNum.length === 12 || cleanNum.length === 10 || cleanNum.length === 6) {
      continue; // Exclude Aadhaar/Phone/PIN
    }

    addCandidate(matchedStr, day, month, year, matchIndex);
  }

  // Pattern B: Strict YMD Numeric (e.g. 2024-08-15, 2024/08/15, 2024.08.15)
  const ymdRegex = /\b([0-9]{4})[-./\s]+([0-9]{1,2})[-./\s]+([0-9]{1,2})\b/gi;
  while ((match = ymdRegex.exec(normalized)) !== null) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);

    addCandidate(match[0], day, month, year, match.index);
  }

  // Pattern C: Textual Month Dates (e.g. 13th day of August, 1999 / 10-AUG-99 / August 13, 1999)
  for (const [monthName, monthVal] of Object.entries(MONTHS_MAP)) {
    const escaped = monthName.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

    // Day Month Year
    const p1 = new RegExp(`\\b([0-9]{1,2})(?:st|nd|rd|th)?(?:\\s+(?:the\\s+)?day\\s+of)?(?:\\s+of)?[\\s,.-]*${escaped}[\\s,.-]*([0-9]{2,4})\\b`, 'gi');
    // Month Day Year
    const p2 = new RegExp(`\\b${escaped}[\\s,.-]*([0-9]{1,2})(?:st|nd|rd|th)?(?:\\s+(?:the\\s+)?day\\s+of)?(?:\\s+of)?[\\s,.-]*([0-9]{2,4})\\b`, 'gi');

    let m;
    while ((m = p1.exec(textLower)) !== null) {
      const day = parseInt(m[1], 10);
      let year = parseInt(m[2], 10);
      if (year < 100) year += year < 50 ? 2000 : 1900;
      const originalStr = normalized.substring(m.index, m.index + m[0].length);
      addCandidate(originalStr, day, monthVal, year, m.index);
    }
    while ((m = p2.exec(textLower)) !== null) {
      const day = parseInt(m[1], 10);
      let year = parseInt(m[2], 10);
      if (year < 100) year += year < 50 ? 2000 : 1900;
      const originalStr = normalized.substring(m.index, m.index + m[0].length);
      addCandidate(originalStr, day, monthVal, year, m.index);
    }
  }

  return candidates;
};

export const loadPdfDocument = (file, pdfjsLibInstance) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async () => {
      try {
        const pdfData = new Uint8Array(reader.result);
        const pdf = await pdfjsLibInstance.getDocument({ data: pdfData }).promise;
        resolve(pdf);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = (err) => reject(err);
  });
};

export const renderPdfPageToCanvas = async (pdf, pageNumber) => {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 1.8 });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.height = viewport.height;
  canvas.width = viewport.width;

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);

  await page.render({ canvasContext: context, viewport }).promise;
  return canvas;
};

export const getCanvasFromImageSource = (source) => {
  if (source instanceof HTMLCanvasElement) {
    return Promise.resolve(source);
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    };
    img.onerror = (err) => reject(err);
    if (source instanceof File || source instanceof Blob) {
      img.src = URL.createObjectURL(source);
    } else if (typeof source === 'string') {
      img.src = source;
    } else {
      reject(new Error('Unsupported image source type'));
    }
  });
};

export const rotateCanvas = (canvas, degree) => {
  if (degree === 0) return canvas;

  const rotatedCanvas = document.createElement('canvas');
  const ctx = rotatedCanvas.getContext('2d');

  if (degree === 90 || degree === 270) {
    rotatedCanvas.width = canvas.height;
    rotatedCanvas.height = canvas.width;
  } else {
    rotatedCanvas.width = canvas.width;
    rotatedCanvas.height = canvas.height;
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, rotatedCanvas.width, rotatedCanvas.height);

  ctx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
  ctx.rotate((degree * Math.PI) / 180);
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

  return rotatedCanvas;
};

// Main Entry Point for Document Date Extraction
export const extractDateFromDocument = async (file, pdfjsLibInstance = null) => {
  console.log('[DateExtractor] Starting instant date extraction for:', file?.name, 'Type:', file?.type);

  if (!file) {
    return { dateFound: false, date: null, originalDate: null, confidence: 0, verified: false, primaryDate: null };
  }

  const pdfInstance = pdfjsLibInstance || (typeof window !== 'undefined' ? window.pdfjsLib : null);
  const verifiedDates = [];
  let fullDocumentText = '';

  try {
    // 1. PDF Processing
    if (file.type === 'application/pdf' && pdfInstance) {
      let pdf;
      try {
        pdf = await loadPdfDocument(file, pdfInstance);
      } catch (err) {
        console.error('[DateExtractor] Failed to load PDF via pdfjsLib:', err);
      }

      if (pdf) {
        const pageCount = pdf.numPages;

        // Fast Path 1: Extract Native PDF Text on Page 1 first (Instant < 0.1s)
        try {
          const page1 = await pdf.getPage(1);
          const textContent1 = await page1.getTextContent();
          const page1Text = textContent1.items.map(item => item.str).join(' ');
          if (page1Text && page1Text.trim().length > 10) {
            fullDocumentText += ' Page 1: ' + page1Text;
            const p1Verified = extractAllDatesFromText(page1Text, 1, 'PDF_TEXT');
            if (p1Verified.length > 0) {
              verifiedDates.push(...p1Verified);
            }
          }
        } catch (e) {}

        // If Page 1 native text gave dates, no need to check other pages
        if (verifiedDates.length === 0) {
          for (let i = 2; i <= pageCount; i++) {
            try {
              const page = await pdf.getPage(i);
              const textContent = await page.getTextContent();
              const pageText = textContent.items.map(item => item.str).join(' ');
              if (pageText && pageText.trim().length > 10) {
                fullDocumentText += ` Page ${i}: ` + pageText;
                const pageVerified = extractAllDatesFromText(pageText, i, 'PDF_TEXT');
                if (pageVerified.length > 0) {
                  verifiedDates.push(...pageVerified);
                  break;
                }
              }
            } catch (e) {}
          }
        }

        // Fast Path 2: Scanned PDF OCR Fallback (Page 1 first, 0° upright first)
        if (verifiedDates.length === 0) {
          const Tesseract = await loadTesseract();
          const pageOrder = [1];
          for (let p = 2; p <= Math.min(pageCount, 3); p++) pageOrder.push(p);

          for (const p of pageOrder) {
            try {
              const pageCanvas = await renderPdfPageToCanvas(pdf, p);
              // Try upright 0° first (Instant < 0.8s)
              const res0 = await Tesseract.recognize(pageCanvas, 'eng');
              const text0 = res0?.data?.text || '';
              if (text0) {
                fullDocumentText += ` Page ${p} (0°): ` + text0;
                const found0 = extractAllDatesFromText(text0, p, 'OCR');
                if (found0.length > 0) {
                  verifiedDates.push(...found0);
                  break; // Found date on 0°, exit immediately!
                }
              }

              // Try remaining rotations only if 0° yielded no dates
              for (const rotation of [90, 180, 270]) {
                const rotatedCanvas = rotateCanvas(pageCanvas, rotation);
                const resRot = await Tesseract.recognize(rotatedCanvas, 'eng');
                const textRot = resRot?.data?.text || '';
                if (textRot) {
                  const foundRot = extractAllDatesFromText(textRot, p, 'OCR');
                  if (foundRot.length > 0) {
                    verifiedDates.push(...foundRot);
                    break;
                  }
                }
              }

              if (verifiedDates.length > 0) break;
            } catch (ocrPageErr) {
              console.error(`[DateExtractor] OCR failed on PDF page ${p}:`, ocrPageErr);
            }
          }
        }
      }
    } else {
      // 2. Image File Processing (Instant 0° first)
      const Tesseract = await loadTesseract();
      const canvas = await getCanvasFromImageSource(file);

      try {
        const res0 = await Tesseract.recognize(canvas, 'eng');
        const text0 = res0?.data?.text || '';
        if (text0) {
          fullDocumentText += ' Rotation 0°: ' + text0;
          const found0 = extractAllDatesFromText(text0, 1, 'OCR');
          if (found0.length > 0) {
            verifiedDates.push(...found0);
          }
        }
      } catch (e) {}

      if (verifiedDates.length === 0) {
        for (const rotation of [90, 180, 270]) {
          try {
            const rotatedCanvas = rotateCanvas(canvas, rotation);
            const resRot = await Tesseract.recognize(rotatedCanvas, 'eng');
            const textRot = resRot?.data?.text || '';
            if (textRot) {
              const foundRot = extractAllDatesFromText(textRot, 1, 'OCR');
              if (foundRot.length > 0) {
                verifiedDates.push(...foundRot);
                break;
              }
            }
          } catch (e) {}
        }
      }
    }
  } catch (globalErr) {
    console.error('[DateExtractor] Error during document date extraction:', globalErr);
  }

  // Deduplicate verified dates
  const uniqueDatesMap = new Map();
  for (const item of verifiedDates) {
    if (!uniqueDatesMap.has(item.normalized) || item.score > uniqueDatesMap.get(item.normalized).score) {
      uniqueDatesMap.set(item.normalized, item);
    }
  }
  const cleanVerifiedDates = Array.from(uniqueDatesMap.values()).sort((a, b) => b.score - a.score);

  // Requirement 15 Console Logs
  console.log('OCR CANDIDATES:', verifiedDates);
  console.log('VERIFIED DATES:', cleanVerifiedDates);

  console.log('===== VERIFIED DOCUMENT DATES =====');
  cleanVerifiedDates.forEach(d => {
    console.log({
      date: d.normalized,
      original: d.original,
      page: d.page,
      source: d.source,
      verified: true,
      context: d.context
    });
  });
  console.log('===================================');

  // Determine primary date
  // Fulfill explicit user instruction: "first find date or dated date if there read that if not go with regular process"
  let primaryDate = null;
  if (cleanVerifiedDates.length > 0) {
    const datedCandidates = cleanVerifiedDates.filter(c =>
      /\b(dated|datad|date\s*:|dated\s*:|date\s*-|dated\s*-)\b/i.test(c.context || '')
    );
    if (datedCandidates.length > 0) {
      primaryDate = datedCandidates[0]; // Take highest scoring Dated candidate first
    } else {
      const currentYear = new Date().getFullYear();
      const validCandidates = cleanVerifiedDates.filter(c => {
        const yr = parseInt((c.normalized || '').split('-')[0], 10);
        return c.score >= 40 && yr >= 1970 && yr <= (currentYear + 1);
      });
      if (validCandidates.length > 0) {
        primaryDate = validCandidates[0]; // Fallback to regular process
      }
    }
  }

  const dateFound = !!primaryDate;
  const normalizedDate = primaryDate ? primaryDate.normalized : null;
  const originalDateStr = primaryDate ? primaryDate.original : null;

  return {
    dateFound,
    date: normalizedDate,
    originalDate: originalDateStr,
    page: primaryDate ? primaryDate.page : null,
    confidence: primaryDate ? primaryDate.confidence : 0,
    verified: dateFound,
    primaryDate: primaryDate ? {
      original: primaryDate.original,
      normalized: primaryDate.normalized,
      page: primaryDate.page,
      source: primaryDate.source,
      verified: true
    } : null,
    candidates: cleanVerifiedDates.map(c => ({
      date: c.normalized,
      original: c.original,
      page: c.page,
      source: c.source,
      verified: true
    })),
    text: fullDocumentText
  };
};

// Extract Owner Name from document text using legal, salutation, and label patterns
export const extractOwnerNameFromText = (text) => {
  if (!text || typeof text !== 'string') return '';

  const cleanText = text.replace(/\s+/g, ' ');

  // Rule 1: Explicit labels (Purchaser, Owner, In favour of, Transferee, Vendee, Claimant, Applicant, etc.)
  const labelPatterns = [
    /\b(?:owner(?:\s*name)?|purchaser|transferee|vendee|claimant|applicant|holder)\s*[:\-\s]+\s*(?:sri|smt|mr|mrs|dr|ms)?\.?\s*([A-Z][a-zA-Z.\s]{2,40})/i,
    /\b(?:in\s+favou?r\s+of|favouring)\s+(?:sri|smt|mr|mrs|dr|ms)?\.?\s*([A-Z][a-zA-Z.\s]{2,40})/i,
    /\b(?:to\s+sri|to\s+smt|to\s+mr|to\s+mrs)\.?\s+([A-Z][a-zA-Z.\s]{2,40})/i,
    /\b(?:name\s+of\s+the\s+(?:owner|purchaser|applicant|institution|person|muthawalli))\s*[:\-\s]+\s*(?:sri|smt|mr|mrs|dr|ms)?\.?\s*([A-Z][a-zA-Z.\s]{2,40})/i,
    /\b(?:claimed\s+by|issued\s+to|certified\s+that)\s+(?:sri|smt|mr|mrs|dr|ms)?\.?\s*([A-Z][a-zA-Z.\s]{2,40})/i
  ];

  for (const pattern of labelPatterns) {
    const match = cleanText.match(pattern);
    if (match && match[1]) {
      const candidate = match[1].replace(/^(?:sri|smt|mr|mrs|dr|ms)\.?\s+/i, '').trim();
      const cleaned = candidate.split(/[,;\n\r\t]/)[0].replace(/[^a-zA-Z\s.]/g, '').trim();
      if (cleaned.length >= 3 && !/^(the|and|for|with|from|under|section|plot|flat|door|survey|village|mandal|district)$/i.test(cleaned)) {
        return cleaned;
      }
    }
  }

  // Rule 2: Salutation based "Sri / Smt / Mr / Mrs <Name>"
  const salutationPattern = /\b(?:sri|smt|mr|mrs)\.?\s+([A-Z][a-zA-Z.]{1,20}(?:\s+[A-Z][a-zA-Z.]{1,20}){1,3})\b/g;
  let match;
  while ((match = salutationPattern.exec(cleanText)) !== null) {
    const candidate = match[1].trim();
    if (candidate.length >= 3 && !/\b(?:district|mandal|village|hyderabad|telangana|secunderabad|andhra|pradesh|street|road|nagar|colony|plaza|building|flat|plot|survey|office|department|commissioner|secretariat)\b/i.test(candidate)) {
      return candidate;
    }
  }

  return '';
};

// Fast-path Extract Owner Name from Document File (PDF / Image)
export const extractOwnerNameFromDocument = async (file, pdfjsLibInstance = null) => {
  if (!file) return '';

  try {
    const instance = pdfjsLibInstance || (typeof window !== 'undefined' ? window.pdfjsLib : null);

    // 1. PDF File
    if (file.type === 'application/pdf' && instance) {
      const pdf = await loadPdfDocument(file, instance);
      if (pdf && pdf.numPages > 0) {
        // Extract native PDF text on Page 1 first
        try {
          const page1 = await pdf.getPage(1);
          const textContent = await page1.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          if (pageText && pageText.trim().length > 10) {
            const extracted = extractOwnerNameFromText(pageText);
            if (extracted) return extracted;
          }
        } catch (e) {}
      }
    }

    // 2. OCR Fallback for Scanned PDF or Image
    const Tesseract = await loadTesseract();
    let canvas;
    if (file.type === 'application/pdf' && instance) {
      const pdf = await loadPdfDocument(file, instance);
      if (pdf) {
        canvas = await renderPdfPageToCanvas(pdf, 1);
      }
    } else {
      canvas = await getCanvasFromImageSource(file);
    }

    if (canvas) {
      const res0 = await Tesseract.recognize(canvas, 'eng');
      const text0 = res0?.data?.text || '';
      if (text0) {
        const extracted = extractOwnerNameFromText(text0);
        if (extracted) return extracted;
      }
    }
  } catch (err) {
    console.error('[OwnerNameExtractor] Error extracting owner name:', err);
  }

  return '';
};